import React from "react";

import Footer from "../components/Footer/Footer";

import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  styled,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

// Import contact card images
import ContactCard1 from "../assets/contact/contact-card-1.svg";
import ContactCard2 from "../assets/contact/contact-card-2.svg";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "40px 20px",
    maxWidth: "1200px",
    marginTop: "120px",
    margin: "0 auto",
    [theme.breakpoints.down("md")]: {
      padding: "20px",
    },
  },
  title: {
    fontSize: "60px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "40px",
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: "36px",
    fontWeight: "medium",
    textAlign: "center",
    margin: "140px 0 40px 0",
    color: theme.palette.text.primary,
  },
  cardContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "30px",
    margin: "60px 0px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
    },
  },

  addressSection: {
    textAlign: "center",
    margin: "160px 0px 100px 0px",
  },
  addressTitle: {
    fontSize: "26px",
    fontWeight: "bold",
    marginBottom: "16px",
    color: theme.palette.text.primary,
  },
  address: {
    fontSize: "17px",
    color: theme.palette.text.secondary,
    marginBottom: "8px",
  },
  feedbackSection: {
    textAlign: "center",
    marginTop: "80px",
  },
  feedbackTitle: {
    fontSize: "26px",
    fontWeight: "bold",
    marginBottom: "16px",
    color: theme.palette.text.primary,
  },
  feedbackText: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    marginBottom: "16px",
  },
  feedbackEmail: {
    fontSize: "17px",
    color: theme.palette.text.secondary,
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  disclaimerSection: {
    marginTop: "240px",
    padding: "0 20px",
  },
  disclaimerList: {
    listStyle: "decimal",
    padding: "0 0 0 20px",
    margin: 0,
    "& li": {
      color: theme.palette.text.secondary,
      fontSize: "14px",
      marginBottom: "10px",
      paddingLeft: "10px",
      lineHeight: "1.6",
    },
  },
}));

const ContactPage = () => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <div>
      <Box className={classes.root}>
        <div className={classes.title}>Contacting indexx</div>
        <div className={classes.subtitle}>Sales and Product Inquiries</div>

        <div className={classes.cardContainer}>
          <img src={ContactCard1} alt="Steve Brown" />
          <img src={ContactCard2} alt="Rod Fowler" />
        </div>

        <div className={classes.addressSection}>
          <div className={classes.addressTitle}>Corporate Address</div>
          <div className={classes.address}>indexx.ai</div>
          <div className={classes.address}>500 Newport Center Drive</div>
          <div className={classes.address}>Newport Beach</div>
          <div className={classes.address}>CA 92660 United States</div>
          <div className={classes.address}>(949) 228-9079</div>
        </div>

        <div className={classes.feedbackSection}>
          <div className={classes.feedbackTitle}>Questions and Feedbacks</div>
          <div className={classes.feedbackText}>
            Tell us your questions and feedbacks through these emails. We are
            here to help you out.
          </div>
          <a href="mailto:contact@indexx.ai" className={classes.feedbackEmail}>
            contact@indexx.ai
          </a>
        </div>

        <div className={classes.disclaimerSection}>
          <p>Disclaimer:</p>
          <ol className={classes.disclaimerList}>
            <li>
              <strong>Purpose:</strong> This disclaimer outlines the potential
              risks and important information associated with using Indexx.ai
              services. Each service we offer carries its own set of risks. We
              aim to provide a general overview to help you make informed
              decisions when engaging with our platform.
            </li>
            <li>
              <strong>No Personal Advice:</strong> Indexx.ai does not offer
              personalized investment, financial, or trading advice. While we
              may provide factual information or insights into transaction
              procedures and potential risks, any decisions made using our
              products or services are solely your responsibility. We recommend
              consulting with qualified financial advisors to determine the
              appropriateness of any investment or strategy in relation to your
              individual circumstances.
            </li>
            <li>
              <strong>No Monitoring:</strong> Indexx.ai operates as a platform
              provider and does not act as your broker, intermediary, agent, or
              advisor. We do not monitor your use of our services to ensure
              consistency with your financial goals. It is your responsibility
              to assess whether your financial resources and risk tolerance are
              suitable for the activities you engage in on our platform.
            </li>
            <li>
              <strong>No Tax, Regulatory, or Legal Advice:</strong> The taxation
              and legal status of digital assets can vary by jurisdiction. You
              are responsible for understanding and complying with any tax
              obligations arising from your transactions on Indexx.ai. We do not
              provide legal or tax advice. For clarity on your tax status or
              legal obligations, please consult with professional advisors.
            </li>
            <li>
              <strong>Market Risk:</strong> Trading digital assets involves
              significant market risk and price volatility. Values can fluctuate
              rapidly and without warning. Past performance is not indicative of
              future results. Investments can decrease in value, and you may not
              recoup the amount invested.
            </li>
            <li>
              <strong>Liquidity Risk:</strong> Certain digital assets may have
              limited liquidity, making it challenging to sell or exit positions
              promptly. This illiquidity can occur at any time, including during
              periods of rapid price movements.
            </li>
            <li>
              <strong>Fees &amp; Charges:</strong> Our fees and charges are
              detailed on our Fees Page. Indexx.ai reserves the right to update
              these fees periodically. Be aware of all costs associated with
              your transactions, as they can affect your net returns.
            </li>
            <li>
              <strong>Availability Risk:</strong> We strive to maintain
              continuous availability of Indexx.ai services but do not guarantee
              uninterrupted access. Services may experience unplanned outages or
              network congestion, affecting your ability to transact at desired
              times.
            </li>
            <li>
              <strong>Third-Party Risk:</strong> Indexx.ai may collaborate with
              third-party providers, such as payment processors or custodians.
              While we select partners carefully, we are not responsible for any
              losses incurred due to third-party actions or system failures.
            </li>
            <li>
              <strong>Security Risk:</strong> While we implement robust security
              measures, Indexx.ai cannot eliminate all security risks. You are
              responsible for safeguarding your account credentials.
              Transactions in digital assets are irreversible; losses from
              unauthorized transactions may not be recoverable.
            </li>
            <li>
              <strong>Risks Related to Digital Assets:</strong> Digital assets
              and their underlying technologies carry inherent risks, including
              but not limited to:
              <ul
                style={{
                  listStyleType: "disc",
                  marginLeft: "20px",
                  marginTop: "10px",
                }}
              >
                <li>Protocol vulnerabilities or failures.</li>
                <li>
                  Irreversible transactions leading to potential loss from
                  fraudulent or accidental activities.
                </li>
                <li>
                  Technological advancements rendering certain assets obsolete.
                </li>
                <li>Delays in transaction settlements.</li>
                <li>
                  Various forms of cyber-attacks (e.g., phishing, hacking,
                  double spending).
                </li>
              </ul>
            </li>
            <li>
              <strong>Monitoring Risk:</strong> Digital asset markets operate
              24/7. Rapid price changes can occur at any time, including outside
              traditional business hours, potentially impacting the value of
              your holdings.
            </li>
            <li>
              <strong>Communication Risk:</strong> Electronic communications are
              subject to risks such as failures, delays, and security breaches.
              Indexx.ai is not responsible for any issues arising from
              electronic communication failures.
            </li>
            <li>
              <strong>Currency Risk:</strong> Fluctuations in currency exchange
              rates can impact the value of your digital asset investments,
              especially when dealing with assets denominated in foreign
              currencies.
            </li>
            <li>
              <strong>Legal Risk:</strong> Changes in laws and regulations can
              materially affect the value and legality of digital assets. These
              changes are unpredictable and can vary across different
              jurisdictions.
            </li>
          </ol>
        </div>
      </Box>
      <Footer />
    </div>
  );
};

export default ContactPage;
